import alt from "../alt";
import indexBy from "lodash/keyBy";
import extend from "lodash/extend";
import values from "lodash/values";
import PassUtil from "../utils/PassUtil";
import PassActions from "../actions/PassActions";
import UserStore from "../stores/UserStore";
import OrderStore from "../stores/OrderStore";
import OrderActions from "../actions/OrderActions";
import LedgerActions from "../actions/LedgerActions";
import OrderUtil from "../utils/OrderUtil";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

class PassStore {
  constructor() {
    this.bindActions(PassActions);

    this.state = {
      passes: {},
      deletedPasses: {},
      passesAutocomplete: [],
      passSubscriptions: {},
      eventBucketPassesIncludingGlobal: {},
      passTransferHistory: [],
      passTransferHistorySinglePass: {},
      isFetching: false,
      isEditing: false
    };

    this.exportPublicMethods({
      getPassById: this.getPassById.bind(this),
      getPassSubscriptionById: this.getPassSubscriptionById.bind(this),
      getPassesForPerson: this.getPassesForPerson.bind(this),
      getPassesForPassBucket: this.getPassesForPassBucket.bind(this),
      getPassesForEventBucket: this.getPassesForEventBucket.bind(this),
      getPassesForEventBucketIncludingGlobal:
        this.getPassesForEventBucketIncludingGlobal.bind(this),
      getPassesAutocomplete: this.getPassesAutocomplete.bind(this),
      getPassTransferHistory: this.getPassTransferHistory.bind(this),
      getPassTransferHistorySinglePass: this.getPassTransferHistorySinglePass.bind(this),
      getMembershipsForEventBucket: this.getMembershipsForEventBucket.bind(this),
    });
  }

  getPassById(id) {
    return this.state.passes[id];
  }

  getPassSubscriptionById(id) {
    return this.state.passSubscriptions[id];
  }

  getPassesForPerson(id, eventBucket) {
    return values(this.state.passes).filter(function (p) {
      if (eventBucket && p.event_bucket !== eventBucket) {
        return false;
      }
      return p.person && p.person.id === id;
    });
  }

  getPassesForPassBucket(id) {
    return values(this.state.passes).filter(function (p) {
      return p.pass_bucket.id === id;
    });
  }

  getPassesForEventBucket(id) {
    return values(this.state.passes).filter(function (p) {
      return p.event_bucket === id && !p.pass_bucket.is_membership;
    });
  }

  getMembershipsForEventBucket(id) {
    return values(this.state.passes).filter(function (p) {
      return p.event_bucket === id && p.pass_bucket.is_membership;
    });
  }

  getPassesForEventBucketIncludingGlobal(id) {
    return this.state.eventBucketPassesIncludingGlobal[id]
      ? this.state.eventBucketPassesIncludingGlobal[id]
          .map(passId => this.state.passes[passId])
          .filter(p => {
            if (!p) {
              return false;
            }

            if (
              p.is_active === false ||
              (p.pass_bucket && p.pass_bucket.hide_from_users)
            ) {
              // This drives Box Office UI - hide hidden or disabled passes
              return false;
            }

            return true;
          })
      : [];
  }

  getPassesAutocomplete() {
    return this.state.passesAutocomplete;
  }

  getPassTransferHistory() {
    return this.state.passTransferHistory;
  }

  getPassTransferHistorySinglePass() {
    return this.state.passTransferHistorySinglePass;
  }

  onFetchAllForPassBucket(bucketId) {
    this.setState({ isFetching: true });
    PassUtil.fetchAllForPassBucket(UserStore.getState().apiKey, bucketId)
      .then(data => {
        PassActions.updatePasses(indexBy(data, "id"));
        this.setState({ isFetching: false });
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchAllForPerson(personId) {
    this.setState({ isFetching: true });
    PassUtil.fetchAllForPerson(UserStore.getState().apiKey, personId)
      .then(data => {
        PassActions.updatePasses(indexBy(data, "id"));
        this.setState({ isFetching: false });
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchAllForEventBucket(eventBucketId) {
    this.setState({ isFetching: true });
    PassUtil.fetchAllForEventBucket(UserStore.getState().apiKey, eventBucketId)
      .then(data => {
        PassActions.updatePasses(indexBy(data, "id"));
        this.setState({ isFetching: false });
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchAllForEventBucketIncludingGlobal(eventBucketId) {
    this.setState({ isFetching: true });
    PassUtil.fetchAllForEventBucketIncludingGlobal(
      UserStore.getState().apiKey,
      eventBucketId
    )
      .then(data => {
        this.state.eventBucketPassesIncludingGlobal[eventBucketId] = data.map(
          d => d.id
        );
        this.setState({
          eventBucketPassesIncludingGlobal:
            this.state.eventBucketPassesIncludingGlobal,
          isFetching: false
        });
        PassActions.updatePasses(indexBy(data, "id"));
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchOne(id) {
    this.setState({ isFetching: true });
    PassUtil.fetchOne(UserStore.getState().apiKey, id)
      .then(data => {
        PassActions.updatePass(id, data);
        this.setState({ isFetching: false });
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchPassSubscription(id) {
    PassUtil.fetchPassSubscription(UserStore.getState().apiKey, id)
      .then(data => {
        PassActions.updatePassSubscription(id, data);
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchAutocomplete(values) {
    this.setState({ isFetching: true });
    PassUtil.fetchAutocomplete(
      UserStore.getState().apiKey,
      values[0],
      values[1]
    )
      .then(data => {
        PassActions.updateAutocomplete(data);
        this.setState({ isFetching: true });
      })
      .catch(PassActions.fetchFailed);
  }

  onFetchPassTransferHistory(ids) {
    this.setState({ isFetching: true });
    PassUtil.fetchPassTransferHistory(UserStore.getState().apiKey, ids.passId, ids.eventBucketId)
      .then(data => {
        this.setState({ passTransferHistory: data.history, passTransferHistorySinglePass: data.pass, isFetching: false });
      })
      .catch(PassActions.fetchFailed);
  }

  onUpdateAutocomplete(data) {
    if (!data) {
      this.state.passesAutocomplete = [];
    } else {
      this.state.passesAutocomplete = data.data.passes;
    }
    this.setState(this.state);
  }

  onEdit(data) {
    this.setState({ isEditing: true });
    PassUtil.edit(UserStore.getState().apiKey, data.id, data.values)
      .then(data => {
        PassActions.updatePass(data.id, data);

        // Dispatch order update if order has been fetched previously
        // (to propagate updated pass if viewing orders page).
        let order = OrderStore.getState().orders[data.order];
        if (order) {
          OrderActions.fetchOne(data.order);
        }

        this.setState({ isEditing: false });
      })
      .catch(PassActions.editFailed);
  }

  onDeletePasses(form) {
    this.setState({ isFetching: true });

    const passes = { ...this.state.passes };
    PassUtil.deleteBulk(UserStore.getState().apiKey, UserStore.getCredentials().api_key, form)
      .then(data => {
        data.passIds.map(passId => {
          this.state.deletedPasses[passId] = true;
        });

        this.setState({
          isFetching: false,
          passes,
          deletedPasses: this.state.deletedPasses
        });
      })
      .catch(PassActions.fetchFailed);
  }

  onRefundPass({ orderId, options }) {
    OrderUtil.refund(UserStore.getState().apiKey, orderId, {
      refunds: options
    })
      .then(data => {
        console.log(data);
        for (const option in options) {
          if (!option.startsWith("PASS")) continue;

          const optionData = options[option];

          const passId = option.replace("PASS-", "");

          if (optionData.delete) {
            this.state.deletedPasses[passId] = true;
          }

          OrderActions.updateOrder(data);
          LedgerActions.fetchLedgerTransactionsForOrder.defer(orderId); // force ledger update

          this.setState({
            deletedPasses: this.state.deletedPasses
          });
        }
      })
      .catch(PassActions.fetchFailed);
  }

  onBulkTransferPasses(form) {
    this.setState({ isFetching: true });

    const passes = { ...this.state.passes };
    PassUtil.transferBulk(UserStore.getState().apiKey, form)
      .then(data => {
        data.transfers.map(pass => {
          delete passes[pass.id];
          passes[pass.id] = { ...pass };
        });

        this.setState({ isFetching: false, passes });
      })
      .catch(PassActions.fetchFailed);
  }

  onTransfer({ id, recipient }) {
    PassUtil.transfer(UserStore.getState().apiKey, id, recipient)
      .then(data => {
        PassActions.updatePass(data.id, data);

        // Dispatch order update if order has been fetched previously
        // (to propagate updated pass if viewing orders page).
        let order = OrderStore.getState().orders[data.order];
        if (order) {
          OrderActions.fetchOne(data.order);
        }
      })
      .catch(PassActions.editFailed);
  }

  onCancelTransfer(id) {
    PassUtil.cancelTransfer(UserStore.getState().apiKey, id)
      .then(data => {
        PassActions.updatePass(data.id, data);

        // Dispatch order update if order has been fetched previously
        // (to propagate updated pass if viewing orders page).
        let order = OrderStore.getState().orders[data.order];
        if (order) {
          OrderActions.fetchOne(data.order);
        }
      })
      .catch(PassActions.editFailed);
  }

  onBulkCancelPassTransfers(passIds) {
    this.setState({ isFetching: true });

    const passes = { ...this.state.passes };
    PassUtil.cancelTransfersBulk(UserStore.getState().apiKey, passIds)
      .then(data => {
        data.passes.map(pass => {
          delete passes[pass.id];
          passes[pass.id] = { ...pass };
        });

        this.setState({ isFetching: false, passes });
      })
      .catch(PassActions.fetchFailed);
  }

  onDelete(id) {
    PassUtil.delete(UserStore.getState().apiKey, UserStore.getCredentials().api_key, id)
      .then(data => {
        delete this.state.passes[id];
        this.state.deletedPasses[id] = true;
        this.setState(this.state);
      })
      .catch(PassActions.deleteFailed);
  }

  onUpdatePass(data) {
    this.state.passes[data.id] = data.pass;
    this.setState(this.state);
  }

  onUpdatePasses(passes) {
    extend(this.state.passes, passes);
    this.setState({ passes: this.state.passes });
  }

  onUpdatePassSubscription(data) {
    this.state.passSubscriptions[data.id] = data.pass_subscription;
    this.setState({ passSubscriptions: this.state.passSubscriptions });
  }

  onEditPassSubscription(data) {
    this.setState({ isEditing: true });

    PassUtil.editPassSubscription(
      UserStore.getState().apiKey,
      data.id,
      data.values
    )
      .then(data => {
        PassActions.updatePassSubscription(data.id, data);

        // Dispatch pass updates
        data.passes
          .map(p => p.id)
          .map(passId => {
            PassActions.updatePass(
              passId,
              Object.assign({}, this.getPassById(passId), {
                pass_subscription: data
              })
            );
          });

        // Dispatch order updates if order has been fetched previously
        // (to propagate updated pass renewal statuses if viewing orders page).
        data.orders.map(orderId => {
          let order = OrderStore.getState().orders[orderId];
          if (order) {
            OrderActions.fetchOne(orderId);
          }
        });

        this.setState({ isEditing: false });
      })
      .catch(PassActions.editFailed);
  }

  onFetchFailed(e) {
    this.setState({ isFetching: false });
    console.error("Fetching pass failed:", e);
    GlobalNotificationActions.notify.defer(
      `Fetching pass failed: ${e.message}`
    );
    throw e;
  }

  onEditFailed(e) {
    this.setState({ isEditing: false });
    alert(`Edit failed: ${e.message}`);
    // throw e;
  }

  onDeleteFailed(e) {
    alert(`Delete failed: ${e.message}`);
    throw e;
  }
}

export default alt.createStore(PassStore, "PassStore");



// WEBPACK FOOTER //
// ./src/app/stores/PassStore.js